import React, { useMemo, useState } from "react";
import { cn } from "../lib/utils";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDate = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const fmtDay = (d) =>
  `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;

const monthName = (m) =>
  ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m];

/**
 * Lightweight Gantt strip for service requests.
 *
 * Props:
 *   items: [{ id, label, sublabel, start_date, end_date, color, status }]
 *   colorClass(item) -> tailwind classes for the bar (optional). If not provided we pick from status / color.
 *   onSelect(item) -> click handler (optional).
 *   emptyHint: shown when there are no items.
 */
export default function RequestsGantt({ items = [], onSelect, colorClass, emptyHint = "No requests yet." }) {
  const today = useMemo(() => parseDate(new Date().toISOString().slice(0, 10)), []);

  // Items split into "scheduled" (have at least one date) and "no-date" (skipped from chart).
  const dated = items.filter((i) => parseDate(i.start_date) || parseDate(i.end_date));
  const undated = items.filter((i) => !parseDate(i.start_date) && !parseDate(i.end_date));

  // Compute window from min start to max end. Pad 3 days on each side. Min span 14 days.
  const { rangeStart, rangeEnd } = useMemo(() => {
    if (dated.length === 0) {
      const start = addDays(today, -7);
      const end = addDays(today, 21);
      return { rangeStart: start, rangeEnd: end };
    }
    let min = null;
    let max = null;
    dated.forEach((it) => {
      const s = parseDate(it.start_date) || parseDate(it.end_date);
      const e = parseDate(it.end_date) || parseDate(it.start_date);
      if (s && (!min || s < min)) min = s;
      if (e && (!max || e > max)) max = e;
    });
    if (today < min) min = today;
    if (today > max) max = today;
    const start = addDays(min, -3);
    const end = addDays(max, 3);
    const span = Math.round((end - start) / DAY_MS);
    if (span < 14) {
      return { rangeStart: start, rangeEnd: addDays(start, 14) };
    }
    return { rangeStart: start, rangeEnd: end };
  }, [dated, today]);

  const totalDays = Math.max(1, Math.round((rangeEnd - rangeStart) / DAY_MS) + 1);

  // Build day-level grid for headers. Week if range > 35 days else day cells.
  const showDays = totalDays <= 45;

  const headerCells = [];
  if (showDays) {
    for (let i = 0; i < totalDays; i++) {
      const d = addDays(rangeStart, i);
      headerCells.push({ key: i, date: d, label: String(d.getDate()), isMonthStart: d.getDate() === 1 || i === 0 });
    }
  } else {
    // Group by week (start Monday).
    let cursor = new Date(rangeStart);
    while (cursor <= rangeEnd) {
      headerCells.push({
        key: cursor.toISOString(),
        date: new Date(cursor),
        label: `${monthName(cursor.getMonth())} ${cursor.getDate()}`,
        isMonthStart: cursor.getDate() <= 7,
      });
      cursor = addDays(cursor, 7);
    }
  }

  const dayWidth = showDays ? 28 : 80; // px per cell
  const gridWidth = showDays ? totalDays * dayWidth : headerCells.length * dayWidth;

  const barLeft = (start) => Math.max(0, Math.round((start - rangeStart) / DAY_MS)) * (showDays ? dayWidth : dayWidth / 7);
  const barWidth = (start, end) => {
    const days = Math.max(1, Math.round((end - start) / DAY_MS) + 1);
    return days * (showDays ? dayWidth : dayWidth / 7);
  };

  const todayLeft = (today >= rangeStart && today <= rangeEnd)
    ? Math.round((today - rangeStart) / DAY_MS) * (showDays ? dayWidth : dayWidth / 7)
    : null;

  const labelCol = 220;

  const defaultColor = (item) => {
    const s = (item.status || "").toLowerCase();
    if (s === "approved") return "bg-emerald-500/30 border-emerald-500/60 text-emerald-200";
    if (s === "rejected") return "bg-red-500/20 border-red-500/40 text-red-300 opacity-60";
    if (s === "pending") return "bg-amber-500/30 border-amber-500/60 text-amber-100";
    return "bg-blue-500/30 border-blue-500/60 text-blue-100";
  };

  if (items.length === 0) {
    return <p className="text-xs text-zinc-500 text-center py-4">{emptyHint}</p>;
  }

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded border border-zinc-800 bg-zinc-950/50">
        <div style={{ width: labelCol + gridWidth }} className="relative">
          {/* Header */}
          <div className="flex sticky top-0 z-10 bg-zinc-900 border-b border-zinc-800">
            <div style={{ width: labelCol }} className="shrink-0 px-3 py-2 text-[10px] uppercase tracking-wide text-zinc-500 border-r border-zinc-800">
              Service
            </div>
            <div className="flex" style={{ width: gridWidth }}>
              {headerCells.map((cell) => (
                <div
                  key={cell.key}
                  style={{ width: dayWidth }}
                  className={cn(
                    "shrink-0 text-[10px] text-zinc-400 px-1 py-1 border-r border-zinc-800/50 text-center",
                    cell.isMonthStart && "text-zinc-200 font-medium"
                  )}
                >
                  {cell.isMonthStart && showDays && (
                    <div className="text-[9px] text-zinc-500 leading-none">{monthName(cell.date.getMonth())}</div>
                  )}
                  <div className="leading-tight">{cell.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Rows */}
          {dated.map((item) => {
            const start = parseDate(item.start_date) || parseDate(item.end_date);
            const end = parseDate(item.end_date) || parseDate(item.start_date);
            const left = barLeft(start);
            const width = barWidth(start, end);
            const cls = colorClass ? colorClass(item) : defaultColor(item);

            return (
              <div key={item.id} className="flex border-b border-zinc-800/60 hover:bg-zinc-900/40">
                <div style={{ width: labelCol }} className="shrink-0 px-3 py-2 border-r border-zinc-800 min-w-0">
                  <p className="text-xs text-zinc-50 truncate">{item.label}</p>
                  {item.sublabel && <p className="text-[10px] text-zinc-500 truncate">{item.sublabel}</p>}
                </div>
                <div className="relative" style={{ width: gridWidth, height: 36 }}>
                  {/* Today marker */}
                  {todayLeft !== null && (
                    <div
                      className="absolute top-0 bottom-0 w-px bg-orange-500/60 pointer-events-none z-10"
                      style={{ left: todayLeft + (showDays ? dayWidth / 2 : dayWidth / 14) }}
                    />
                  )}
                  {/* Bar */}
                  <button
                    type="button"
                    onClick={() => onSelect && onSelect(item)}
                    title={`${fmtDay(start)} → ${fmtDay(end)}`}
                    className={cn(
                      "absolute top-1.5 bottom-1.5 rounded border px-2 flex items-center text-[10px] font-medium overflow-hidden whitespace-nowrap text-left transition-opacity",
                      cls,
                      onSelect ? "cursor-pointer hover:brightness-110" : "cursor-default"
                    )}
                    style={{ left, width }}
                  >
                    {fmtDay(start)} – {fmtDay(end)}
                  </button>
                </div>
              </div>
            );
          })}

          {/* Today marker on header (only if no dated items) */}
          {dated.length === 0 && todayLeft !== null && (
            <div className="flex border-b border-zinc-800/60">
              <div style={{ width: labelCol }} className="shrink-0 px-3 py-3 border-r border-zinc-800 text-xs text-zinc-500">
                No dates yet
              </div>
              <div className="relative" style={{ width: gridWidth, height: 36 }}>
                <div
                  className="absolute top-0 bottom-0 w-px bg-orange-500/60"
                  style={{ left: todayLeft + (showDays ? dayWidth / 2 : dayWidth / 14) }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Legend / undated */}
      <div className="flex items-center justify-between flex-wrap gap-2 text-[10px] text-zinc-500 px-1">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500/60" /> Pending</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500/60" /> Approved</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500/40" /> Rejected</span>
          <span className="flex items-center gap-1"><span className="w-px h-3 bg-orange-500/60" /> Today</span>
        </div>
        {undated.length > 0 && (
          <span className="text-zinc-500">
            {undated.length} request{undated.length > 1 ? "s" : ""} without dates
          </span>
        )}
      </div>
    </div>
  );
}
